import { existingIdsByField, flagsForIds } from "../existingIdsByField";

/**
 * Receipt / sale id used as `Sales.transaction_id`.
 *
 * Must stay aligned with backend Depop `mapSales` /
 * `MRGBackendAPI/.../depop/mappings.js` (`purchaseId` → `transaction_id`).
 */
export function getDepopReceiptId(receipt: unknown): string | null {
  const record = receipt as Record<string, unknown>;
  const id = record?.purchaseId ?? record?.transaction_id;
  return id == null ? null : String(id);
}

/**
 * Listing id aligned with Inventory.listing_id.
 *
 * Must stay aligned with backend `getDepopListingId` in
 * `MRGBackendAPI/.../depop/mappings.js` (prefer slug from list or edit-listing).
 */
export function getDepopListingId(product: unknown): string | null {
  const record = product as Record<string, unknown>;
  const id = record?.slug ?? record?.id ?? record?.listing_id;
  return id == null ? null : String(id);
}

/** Direct/initial resume: sales already booked under this import's Uploads. */
export async function salesSyncedFlagsForPage(
  receipts: unknown[],
  uploadSalesId: string,
  uploadReturnsId: string
): Promise<boolean[]> {
  const ids = receipts.map((receipt) => getDepopReceiptId(receipt));
  const unique = [...new Set(ids.filter((id): id is string => !!id))];
  const found = await existingIdsByField({
    collection: "Sales",
    idField: "transaction_id",
    ids: unique,
    uploadIds: [uploadSalesId, uploadReturnsId],
  });
  return flagsForIds(ids, found);
}

/**
 * Daily sync: whether each receipt already has a Depop sale booked (any upload).
 * Uses `user` + `transaction_id` with in-memory `import_platform === "depop"`.
 */
export async function salesExistFlagsForPage(
  receipts: unknown[]
): Promise<boolean[]> {
  const ids = receipts.map((receipt) => getDepopReceiptId(receipt));
  const unique = [...new Set(ids.filter((id): id is string => !!id))];
  const found = await existingIdsByField({
    collection: "Sales",
    idField: "transaction_id",
    ids: unique,
    importPlatform: "depop",
  });
  return flagsForIds(ids, found);
}

/** Direct/initial resume: inventory already booked under this import's Upload. */
export async function inventorySyncedFlagsForPage(
  items: unknown[],
  uploadInventoryId: string
): Promise<boolean[]> {
  const ids = items.map((item) => getDepopListingId(item));
  const unique = [...new Set(ids.filter((id): id is string => !!id))];
  const found = await existingIdsByField({
    collection: "Inventory",
    idField: "listing_id",
    ids: unique,
    uploadIds: [uploadInventoryId],
  });
  return flagsForIds(ids, found);
}

/**
 * Filter a page to rows that still need enrich + upload. Depop resume always
 * restarts list paging from the start (opaque cursors are not durable), so
 * every page is filtered by Upload-scoped existence flags.
 */
export function unsyncedItemsFromFlags<T>(
  items: T[],
  syncedFlags: boolean[]
): T[] {
  return items.filter((_, i) => !syncedFlags[i]);
}
